import { getCurrentInstance, onUnmounted } from 'vue';
import { groupByOption } from '@/types/groupByOption';
import getCurrentDateInSeconds from '@/utilities/getCurrentDateInSeconds';
import { getSprintKey } from '@/utilities/sprintKeys';
import { Card } from '@superthread-com/api';

// Keeps track of the latest version of operations per card, outside component reactivity
const operationVersions = new Map<string, number>();

// Utility function to get the current version for a card
const getCardVersion = (cardId: string): number => {
  return operationVersions.get(cardId) ?? 0;
};

// Utility function to increment the version for a card
const incrementCardVersion = (cardId: string): number => {
  const currentVersion = getCardVersion(cardId);
  const newVersion = currentVersion + 1;
  operationVersions.set(cardId, newVersion);
  return newVersion;
};

export default function useHandleMoveCardGroupBy() {
  const _this = getCurrentInstance()?.proxy;
  if (!_this) return;

  // When component is unmounted, cleanup versions for any cards this component was handling
  const handledCards = new Set<string>();
  onUnmounted(() => {
    // Clean up operation versions
    handledCards.forEach((cardId) => {
      operationVersions.delete(cardId);
    });
    handledCards.clear();
  });

  // Helper to create delayed operations that respect versioning
  const createDelayedOperation = (
    cardId: string,
    version: number,
    operation: () => Promise<any> | void,
    delay = 1000
  ) => {
    setTimeout(() => {
      // Only execute if this is still the latest version
      if (getCardVersion(cardId) === version) {
        operation();
      }
    }, delay);
  };

  const removeCardMembers = async (card: Card) => {
    if (!card?.id) return;

    const cardId = card.id;
    const members = card?.members || [];

    if (members.length === 0) {
      return;
    }

    // First update UI immediately
    for (const m of members) {
      localRemoveMemberFromCard(m, cardId);
    }

    // Then make API calls for each member
    for (const m of members) {
      try {
        await _this.$store.dispatch('removeMemberFromCard', {
          memberToRemove: m,
          cardId: cardId,
        });
      } catch (err) {
        console.error('Error removing member', m, 'from card', cardId, err);
      }
    }
  };

  const handleDirectMemberRemoval = (
    originalListId: string,
    cardId: string,
    operationVersion: number
  ) => {
    if (!cardId || !originalListId) return;

    const fakeMember = {
      user_id: originalListId,
      assigned_date: getCurrentDateInSeconds(),
    };

    // Make an immediate API call
    createDelayedOperation(
      cardId,
      operationVersion,
      () => {
        if (getCardVersion(cardId) !== operationVersion) return;
        _this.$store.dispatch('removeMemberFromCard', {
          memberToRemove: fakeMember,
          cardId,
        });
      },
      0 // No delay
    );
  };

  const removeCardTags = async (card: Card) => {
    if (!card?.id) return;

    const cardId = card.id;
    const version = getCardVersion(cardId);

    for (const t of card?.tags || []) {
      await _this.$store.dispatch('localDeleteTagFromCard', { cardId: cardId, tagId: t.id });

      if (getCardVersion(cardId) === version) {
        await _this.$store.dispatch('removeCardTag', { cardId: cardId, tagId: t.id }).catch(() => {
          if (getCardVersion(cardId) === version) {
            _this.$store.dispatch('localAddTagToCard', {
              cardId: cardId,
              tagForRemove: t,
            });
          }
        });
      }
    }
  };

  const localAddMemberToCard = (destinationListId: string, cardId: string) => {
    if (!cardId) return;
    _this.$store.dispatch('localAddMemberToCard', {
      cardId,
      memberToAdd: {
        user_id: destinationListId,
        assigned_date: getCurrentDateInSeconds(),
      },
    });
  };

  const localRemoveMemberFromCard = (memberToRemove: any, cardId: string) => {
    if (!cardId) return;
    _this.$store.dispatch('localRemoveMemberFromCard', { cardId, memberToRemove });
  };

  const localRemoveMemberFromCardBoardView = (cardId: string, memberId: string) => {
    if (!cardId || !memberId) return;
    return _this.$store.dispatch('localRemoveMemberFromCardBoardView', { cardId, memberId });
  };

  const localAddTagToCard = (tag: any, cardId: string) => {
    if (!cardId || !tag) return;
    return _this.$store.dispatch('localAddTagToCard', { tag, cardId });
  };

  const localDeleteTagFromCard = (tagId: string, cardId: string) => {
    if (!cardId || !tagId) return;
    return _this.$store.dispatch('localDeleteTagFromCard', { tagId, cardId });
  };

  const localHandleAssigneeGroupBy = (
    originalListId: string,
    destinationListId: string,
    card: Card
  ) => {
    if (!card?.id) return;

    const cardId = card.id;
    const cardMembers = [...(card.members || [])];
    const memberToRemove = cardMembers.find((m) => m.user_id === originalListId);
    const unassignedList = `${getResourceId(card.project_id, card.board_id, card.sprint_id)}-unassigned`;

    if (originalListId === unassignedList) {
      localAddMemberToCard(destinationListId, cardId);
    } else if (destinationListId === unassignedList) {
      if (cardMembers.length > 1) {
        removeCardMembers(card);
        return;
      }
      if (memberToRemove) {
        localRemoveMemberFromCard(memberToRemove, cardId);
      }
    } else if (memberToRemove?.user_id) {
      localRemoveMemberFromCardBoardView(cardId, memberToRemove.user_id);
      localRemoveMemberFromCard(memberToRemove, cardId);
      localAddMemberToCard(destinationListId, cardId);
    }
  };

  const localHandleTagGroupBy = (originalListId: string, destinationListId: string, card: Card) => {
    if (!card?.id) return;

    const cardId = card.id;
    const tagForAdd = _this.$store.getters.getTags[destinationListId];
    const tagForRemove = _this.$store.getters.getTags[originalListId];
    const cardTags = [...(card?.tags || [])];
    const notagsList = `${getResourceId(card.project_id, card.board_id, card.sprint_id)}-notags`;

    if (originalListId === notagsList) {
      localAddTagToCard(tagForAdd, cardId);
    } else if (destinationListId === notagsList) {
      if (cardTags.length > 1) {
        removeCardTags(card);
        return;
      }
      localDeleteTagFromCard(tagForRemove.id, cardId);
    } else {
      localDeleteTagFromCard(tagForRemove.id, cardId);
      localAddTagToCard(tagForAdd, cardId);
    }
  };

  const handleRemoveTagGroupBy = (
    originalListId: string,
    destinationListId: string,
    card: Card,
    operationVersion: number
  ) => {
    if (!card?.id) return;

    const cardId = card.id;
    const tagForRemove = _this.$store.getters.getTags[originalListId];
    const cardTags = [...(card?.tags || [])];
    const notagsList = `${getResourceId(card.project_id, card.board_id, card.sprint_id)}-notags`;

    if (originalListId === notagsList) return;

    if (destinationListId === notagsList && cardTags.length > 1) {
      removeCardTags(card);
      return;
    }

    // Create delayed operation for API call
    createDelayedOperation(cardId, operationVersion, () => {
      if (getCardVersion(cardId) !== operationVersion) return;
      _this.$store.dispatch('removeCardTag', { tagId: tagForRemove.id, cardId });
    });
  };

  const handleAddTag = (tagId: string, cardId: string, operationVersion: number) => {
    if (!cardId || !tagId) return;

    // Create delayed operation for API call
    createDelayedOperation(cardId, operationVersion, () => {
      if (getCardVersion(cardId) !== operationVersion) return;
      _this.$store.dispatch('addTagToCard', { tagId, cardId });
    });
  };

  const handleAddAssignee = (
    destinationListId: string,
    cardId: string,
    operationVersion: number
  ) => {
    if (!cardId) return;

    // Create delayed operation for API call
    createDelayedOperation(cardId, operationVersion, () => {
      if (getCardVersion(cardId) !== operationVersion) return;

      _this.$store.dispatch('addMemberToCard', {
        memberToAdd: {
          user_id: destinationListId,
          assigned_date: getCurrentDateInSeconds(),
        },
        cardId,
      });
    });
  };

  const handleAddPriority = (
    destinationListId: string,
    cardId: string,
    operationVersion: number
  ) => {
    if (!cardId) return;

    // Create delayed operation for API call
    createDelayedOperation(cardId, operationVersion, () => {
      if (getCardVersion(cardId) !== operationVersion) return;

      _this.$store.dispatch('updateCard', {
        cardId,
        card: { [groupByOption.priority]: destinationListId },
      });
    });
  };

  const localHandlePriorityGroupBy = (
    originalListId: string,
    destinationListId: string,
    card: Card
  ) => {
    if (!card?.id) return;

    const boardId = getResourceId(card.project_id, card.board_id, card.sprint_id);
    _this.$store.dispatch('localUpdateCardField', {
      cardId: card.id,
      fieldName: groupByOption.priority,
      fieldValue: destinationListId,
    });
    _this.$store.dispatch('addCardToGroupByList', {
      boardId,
      epicId: card.epic?.id,
      cardId: card.id,
      listId: destinationListId,
      groupBy: groupByOption.priority,
    });
    _this.$store.dispatch('removeCardFromGroupByList', {
      boardId,
      epicId: card.epic?.id,
      cardId: card.id,
      listId: originalListId,
      groupBy: groupByOption.priority,
    });
  };

  const localHandleEpicGroupBy = (
    originalListId: string,
    destinationListId: string,
    card: Card
  ) => {
    if (!card?.id) return;

    const boardId = getResourceId(card.project_id, card.board_id, card.sprint_id);

    _this.$store.dispatch('addCardToGroupByList', {
      boardId,
      cardId: card.id,
      listId: destinationListId,
      groupBy: groupByOption.epic,
    });
    _this.$store.dispatch('removeCardFromGroupByList', {
      boardId,
      cardId: card.id,
      listId: originalListId,
      groupBy: groupByOption.epic,
    });
  };

  const handleUpdateEpic = async (
    originalListId: string,
    destinationListId: string,
    fullCard: Card,
    operationVersion: number
  ) => {
    if (!fullCard?.id) return;

    const cardId = fullCard.id;
    const cardAlreadyHasEpic = fullCard.epic?.id;
    const noprojectList = `${getResourceId(fullCard.project_id, fullCard.board_id, fullCard.sprint_id)}-noproject`;

    // Create delayed operation for API calls
    createDelayedOperation(cardId, operationVersion, async () => {
      try {
        if (getCardVersion(cardId) !== operationVersion) return;

        if (originalListId === noprojectList) {
          await _this.$store.dispatch('addCardToEpic', {
            cardId,
            epicId: destinationListId,
          });
        } else if (destinationListId === noprojectList) {
          await _this.$store.dispatch('removeCardFromEpic', cardId);
        } else {
          if (cardAlreadyHasEpic) {
            await _this.$store.dispatch('removeCardFromEpic', cardId);
          }

          // Check version again after the first operation
          if (getCardVersion(cardId) !== operationVersion) return;

          await _this.$store.dispatch('addCardToEpic', {
            cardId,
            epicId: destinationListId,
          });
        }
      } catch (error) {
        console.error('Epic update error:', error);
      }
    });
  };

  const handleGroupByDrag = (
    originalListId: string,
    destinationListId: string,
    card: Card,
    groupBy: groupByOption,
    newIndex: number
  ) => {
    try {
      if (!card?.id) {
        console.error('Invalid card in handleGroupByDrag', card);
        return;
      }

      const cardId = card.id;

      // Register that we've handled this card
      handledCards.add(cardId);

      // Increment version to invalidate any previous operations
      const operationVersion = incrementCardVersion(cardId);

      // Create a resource ID for lookups
      const resourceId = getResourceId(card.project_id, card.board_id, card.sprint_id);

      // Define variables outside switch statement to avoid lexical declaration errors
      const unassignedList = `${resourceId}-unassigned`;
      const notagsList = `${resourceId}-notags`;
      let isMovingToUnassigned, isComingFromUnassigned, tagForAdd;

      switch (groupBy) {
        case groupByOption.assignee:
          isMovingToUnassigned = destinationListId === unassignedList;
          isComingFromUnassigned = originalListId === unassignedList;

          // First process local UI changes
          localHandleAssigneeGroupBy(originalListId, destinationListId, card);

          // Handle different cases based on source and destination
          if (isMovingToUnassigned && !isComingFromUnassigned) {
            // Moving from a member list to unassigned - need to remove member(s)
            // ALWAYS make a direct API call to remove the specific member from the original list
            handleDirectMemberRemoval(originalListId, cardId, operationVersion);

            // If the card has multiple members, also try to remove them all
            if (card.members && Array.isArray(card.members) && card.members.length > 0) {
              removeCardMembers(card);
            }
          } else if (!isComingFromUnassigned) {
            // Moving from one member list to another
            // First remove the member from the original list
            handleDirectMemberRemoval(originalListId, cardId, operationVersion);

            // Then add the new member
            handleAddAssignee(destinationListId, cardId, operationVersion);
          } else if (isComingFromUnassigned) {
            // Moving from unassigned to a member list - just add the new member
            handleAddAssignee(destinationListId, cardId, operationVersion);
          }
          break;

        case groupByOption.tag:
          handleRemoveTagGroupBy(originalListId, destinationListId, card, operationVersion);
          localHandleTagGroupBy(originalListId, destinationListId, card);

          if (destinationListId !== notagsList) {
            tagForAdd = _this.$store.getters.getTags[destinationListId];
            handleAddTag(tagForAdd.id, cardId, operationVersion);
          }
          break;

        case groupByOption.priority:
          localHandlePriorityGroupBy(originalListId, destinationListId, card);
          handleAddPriority(destinationListId, cardId, operationVersion);
          break;

        case groupByOption.epic:
          localHandleEpicGroupBy(originalListId, destinationListId, card);
          handleUpdateEpic(originalListId, destinationListId, card, operationVersion);
          break;

        default:
          break;
      }

      // This doesn't have timing issues, so we can execute it immediately
      _this.$store.dispatch('updateCardsGroupByOrder', {
        boardId: resourceId,
        originalListId,
        destinationListId,
        cardId,
        groupBy,
        newIndex,
      });
    } catch (error) {
      console.error('Error in handleGroupByDrag:', error);
    }
  };

  const getResourceId = (projectId: any, boardId: any, sprintId: any) => {
    return sprintId ? getSprintKey(projectId, sprintId) : boardId;
  };

  return {
    handleGroupByDrag,
    removeCardMembers,
    removeCardTags,
  };
}
